import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Landmark, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const STATUSES = ["Active", "Dormant", "Closed"];

const empty = {
  bank_name: "",
  branch: "",
  account_number: "",
  ifsc: "",
  account_type: "",
  purpose: "",
  authorized_signatories: "",
  maker_name: "",
  maker_designation: "",
  checker_name: "",
  checker_designation: "",
  approver_name: "",
  approver_designation: "",
  account_status: "Active",
};

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
        {label}
      </div>
      {children}
    </label>
  );
}

const StatusPill = ({ s }) => {
  const map = {
    Active: "bg-[#3a5a40]/10 text-[#3a5a40] border-[#3a5a40]/30",
    Dormant: "bg-[#d4af37]/10 text-[#8a6d1d] border-[#d4af37]/40",
    Closed: "bg-[#9e2a2b]/10 text-[#9e2a2b] border-[#9e2a2b]/30",
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] tracking-wide uppercase border rounded-sm ${map[s] || ""}`}>
      {s || "—"}
    </span>
  );
};

const maskAcct = (a) => {
  if (!a) return "—";
  const s = String(a);
  if (s.length <= 4) return s;
  return "••••" + s.slice(-4);
};

export default function BankAccounts({
  apiBase = "/banks",
  title = "Bank Accounts",
  subtitle = "Treasury",
  intro = "Multiple accounts maintained by the trust — track purpose, signatories, and status.",
  signatoryMode = "executive", // "executive" => exec body checklist; "mca" => Maker/Checker/Approver
  permModule = "banks",
  writableOverride = null, // if non-null, bypass module-based canWrite check
}) {
  const { canWrite } = useAuth();
  const writable = writableOverride === null ? canWrite(permModule) : writableOverride;
  const [items, setItems] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [activeMembers, setActiveMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = async () => {
    const calls = [
      api.get(apiBase),
      api.get("/masters/bank_purpose"),
      api.get("/masters/bank_account_type"),
    ];
    if (signatoryMode === "executive") {
      calls.push(api.get("/bodies/executive_body"));
    }
    const results = await Promise.all(calls);
    const [a, b, t, c] = results;
    setItems(a.data);
    setPurposes(b.data);
    setAccountTypes(t.data || []);
    if (signatoryMode === "executive") {
      setActiveMembers(
        (c.data || [])
          .filter((m) => m.status === "Active" && (m.name || "").trim())
          .sort((x, y) => x.name.localeCompare(y.name)),
      );
    } else {
      setActiveMembers([]);
    }
  };

  useEffect(() => {
    fetchAll().finally(() => setLoading(false));
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({ ...empty, ...b });
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const selectedSigSet = new Set(
    (form.authorized_signatories || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
  );

  const toggleSignatory = (name) => {
    const next = new Set(selectedSigSet);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    // Preserve alphabetical order when serialising
    const ordered = activeMembers
      .map((m) => m.name)
      .filter((n) => next.has(n));
    // Also preserve any legacy free-text names not in the active member list
    const extras = Array.from(next).filter(
      (n) => !activeMembers.some((m) => m.name === n),
    );
    set("authorized_signatories", [...ordered, ...extras].join(", "));
  };

  const save = async () => {
    if (!form.bank_name.trim() || !form.account_number.trim()) {
      toast.error("Bank Name and Account Number are required");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.put(`${apiBase}/${editing.id}`, form);
        setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
        toast.success("Bank account updated");
      } else {
        const { data } = await api.post(apiBase, form);
        setItems((arr) => [data, ...arr]);
        toast.success("Bank account added");
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const b = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`${apiBase}/${b.id}`);
      setItems((arr) => arr.filter((x) => x.id !== b.id));
      toast.success("Bank account removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="p-8 md:p-12 max-w-7xl mx-auto" data-testid="banks-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">
            {subtitle}
          </div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">{title}</h1>
          <p className="mt-2 text-sm text-[#6b655b]">{intro}</p>
        </div>
        <button
          onClick={openCreate}
          data-testid="bank-add-button"
          disabled={!writable}
          title={!writable ? "Read-only — contact a Super Admin for edit access" : undefined}
          className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus size={14} strokeWidth={1.5} /> Add Account
        </button>
      </div>

      {!writable ? (
        <div
          data-testid="banks-readonly-banner"
          className="mb-6 px-4 py-3 border border-[#d4af37]/40 bg-[#d4af37]/5 rounded-sm text-sm text-[#8a6d1d]"
        >
          You have read-only access to Bank Accounts.
        </div>
      ) : null}

      {loading ? (
        <div className="text-[#6b655b] font-serif text-xl">Loading…</div>
      ) : items.length === 0 ? (
        <div
          className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-16 text-center"
          data-testid="banks-empty"
        >
          <Landmark size={32} strokeWidth={1.5} className="mx-auto text-[#234333]" />
          <div className="mt-4 font-serif text-2xl text-[#1c1a17]">No bank accounts on record</div>
          <div className="mt-2 text-sm text-[#6b655b]">Add the first account.</div>
          <button
            onClick={openCreate}
            data-testid="bank-empty-add-button"
            className="mt-6 inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm"
          >
            <Plus size={14} strokeWidth={1.5} /> Add the first account
          </button>
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="banks-table">
            <thead>
              <tr className="bg-[#f9f8f6] text-left">
                {["Bank", "Branch", "Account No.", "IFSC", "Type", "Purpose", "Status", ""].map(
                  (h, i) => (
                    <th
                      key={i}
                      className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold"
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr
                  key={b.id}
                  className="border-t border-[#e5e0d8] hover:bg-[#f9f8f6]/60"
                  data-testid={`bank-row-${b.id}`}
                >
                  <td className="px-4 py-4 text-[#1c1a17]">{b.bank_name}</td>
                  <td className="px-4 py-4 text-[#6b655b]">{b.branch || "—"}</td>
                  <td className="px-4 py-4 font-mono text-[12px] text-[#1c1a17]">
                    {maskAcct(b.account_number)}
                  </td>
                  <td className="px-4 py-4 font-mono text-[12px] text-[#6b655b]">
                    {b.ifsc || "—"}
                  </td>
                  <td className="px-4 py-4 text-[#6b655b]">{b.account_type || "—"}</td>
                  <td className="px-4 py-4 text-[#6b655b]">{b.purpose || "—"}</td>
                  <td className="px-4 py-4">
                    <StatusPill s={b.account_status} />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={() => openEdit(b)}
                      data-testid={`bank-edit-${b.id}`}
                      className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                    >
                      <Pencil size={14} strokeWidth={1.5} /> {writable ? "Edit" : "View"}
                    </button>
                    {writable ? (
                      <button
                        onClick={() => setConfirmDelete(b)}
                        data-testid={`bank-delete-${b.id}`}
                        className="ml-2 inline-flex items-center gap-1 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-2 py-1 rounded-sm"
                      >
                        <Trash2 size={14} strokeWidth={1.5} /> Delete
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-[#e5e0d8] rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="bank-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              {editing ? "Edit Bank Account" : "Add Bank Account"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
            <Field label="Bank Name *">
              <input
                data-testid="bank-name-input"
                className={inputCls}
                value={form.bank_name}
                onChange={(e) => set("bank_name", e.target.value)}
              />
            </Field>
            <Field label="Branch">
              <input
                data-testid="bank-branch-input"
                className={inputCls}
                value={form.branch}
                onChange={(e) => set("branch", e.target.value)}
              />
            </Field>
            <Field label="Account Number *">
              <input
                data-testid="bank-account-input"
                className={inputCls}
                value={form.account_number}
                onChange={(e) => set("account_number", e.target.value)}
              />
            </Field>
            <Field label="IFSC Code">
              <input
                data-testid="bank-ifsc-input"
                className={inputCls}
                value={form.ifsc}
                onChange={(e) => set("ifsc", e.target.value.toUpperCase())}
              />
            </Field>
            <Field label="Account Type">
              <select
                data-testid="bank-type-select"
                className={inputCls}
                value={form.account_type}
                onChange={(e) => set("account_type", e.target.value)}
              >
                <option value="">— Select —</option>
                {accountTypes.map((t) => (
                  <option key={t.id} value={t.name}>
                    {t.name}
                  </option>
                ))}
                {form.account_type && !accountTypes.some((t) => t.name === form.account_type) ? (
                  <option value={form.account_type}>{form.account_type}</option>
                ) : null}
              </select>
            </Field>
            <Field label="Purpose">
              <select
                data-testid="bank-purpose-select"
                className={inputCls}
                value={form.purpose}
                onChange={(e) => set("purpose", e.target.value)}
              >
                <option value="">— Select —</option>
                {purposes.map((p) => (
                  <option key={p.id} value={p.name}>
                    {p.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Authorized Signatories" full>
              {signatoryMode === "mca" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="bank-signatories-mca">
                  <div className="md:col-span-2 text-[11px] text-[#6b655b] -mb-1">
                    Three-tier signatories — Maker initiates, Checker verifies, Approver authorises.
                  </div>
                  <label className="block">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                      Maker — Name
                    </div>
                    <input
                      data-testid="bank-maker-name"
                      className={inputCls}
                      value={form.maker_name}
                      onChange={(e) => set("maker_name", e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                      Maker — Designation
                    </div>
                    <input
                      data-testid="bank-maker-designation"
                      className={inputCls}
                      value={form.maker_designation}
                      onChange={(e) => set("maker_designation", e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                      Checker — Name
                    </div>
                    <input
                      data-testid="bank-checker-name"
                      className={inputCls}
                      value={form.checker_name}
                      onChange={(e) => set("checker_name", e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                      Checker — Designation
                    </div>
                    <input
                      data-testid="bank-checker-designation"
                      className={inputCls}
                      value={form.checker_designation}
                      onChange={(e) => set("checker_designation", e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                      Approver — Name
                    </div>
                    <input
                      data-testid="bank-approver-name"
                      className={inputCls}
                      value={form.approver_name}
                      onChange={(e) => set("approver_name", e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                      Approver — Designation
                    </div>
                    <input
                      data-testid="bank-approver-designation"
                      className={inputCls}
                      value={form.approver_designation}
                      onChange={(e) => set("approver_designation", e.target.value)}
                    />
                  </label>
                </div>
              ) : activeMembers.length === 0 ? (
                <div
                  data-testid="bank-signatories-empty"
                  className="border border-dashed border-[#e5e0d8] rounded-sm p-4 bg-[#f9f8f6] text-sm text-[#6b655b] flex items-start gap-2"
                >
                  <Users size={16} strokeWidth={1.5} className="mt-0.5 text-[#234333]" />
                  <span>
                    No active members found in the Executive Body. Add members there first to mark them as signatories.
                  </span>
                </div>
              ) : (
                <div
                  data-testid="bank-signatories-list"
                  className="border border-[#e5e0d8] rounded-sm bg-white max-h-56 overflow-y-auto divide-y divide-[#efece6]"
                >
                  {activeMembers.map((m) => {
                    const checked = selectedSigSet.has(m.name);
                    return (
                      <label
                        key={m.id}
                        data-testid={`bank-signatory-option-${m.id}`}
                        className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-[#f9f8f6] ${
                          checked ? "bg-[#efece6]/60" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          data-testid={`bank-signatory-checkbox-${m.id}`}
                          checked={checked}
                          onChange={() => toggleSignatory(m.name)}
                          className="w-4 h-4 accent-[#234333]"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-[#1c1a17] truncate">{m.name}</div>
                          {m.designation ? (
                            <div className="text-[11px] text-[#6b655b] truncate">
                              {m.designation}
                            </div>
                          ) : null}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              {signatoryMode === "executive" ? (
                <div className="text-[11px] text-[#6b655b] mt-2" data-testid="bank-signatories-summary">
                  {selectedSigSet.size === 0
                    ? "None selected"
                    : `${selectedSigSet.size} selected · ${Array.from(selectedSigSet).join(", ")}`}
                </div>
              ) : null}
            </Field>
            <Field label="Account Status">
              <select
                data-testid="bank-status-select"
                className={inputCls}
                value={form.account_status}
                onChange={(e) => set("account_status", e.target.value)}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <DialogFooter className="pt-4">
            <button
              onClick={() => setOpen(false)}
              data-testid="bank-cancel-button"
              className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="bank-save-button"
              className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
      >
        <AlertDialogContent className="bg-white border-[#e5e0d8] rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#1c1a17]">
              Delete this account?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b655b]">
              {confirmDelete?.bank_name} ({maskAcct(confirmDelete?.account_number)}) will be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="bank-delete-cancel"
              className="bg-white text-[#1c1a17] border-[#e5e0d8] rounded-sm"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="bank-delete-confirm"
              className="bg-[#9e2a2b] hover:bg-[#7d2122] text-white rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
